/**
 * NATIVE DATE UTILITIES
 * =====================
 * 
 * Comprehensive date utilities built with native browser APIs.
 * Optimized for Supabase integration with PostgreSQL timestamp handling.
 * 
 * Features:
 * - Zero dependencies (no date-fns, moment.js, etc.)
 * - PostgreSQL timestamp compatibility
 * - Internationalization support via Intl API
 * - Type-safe date operations
 * - Supabase-friendly ISO string formatting
 * 
 * These utilities work seamlessly with:
 * - Supabase timestamp fields
 * - PostgreSQL DATE and TIMESTAMP types
 * - Browser date inputs
 * - API serialization/deserialization
 */

// Type definitions
export type DateInput = Date | string | number;

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Convert various date inputs to a Date object.
 *
 * PostgreSQL DATE columns (decisions.decision_date, scheduled_reviews.due_date)
 * arrive over the Data API as bare `YYYY-MM-DD` strings. `new Date('2026-09-13')`
 * follows the ISO rule and parses those as UTC midnight, so every local
 * rendering west of UTC shows the previous calendar day. A calendar date has no
 * time zone, so anchor it at local midnight instead.
 */
export function toDate(input: DateInput): Date {
  if (input instanceof Date) return input;

  if (typeof input === 'string') {
    const dateOnly = DATE_ONLY_PATTERN.exec(input);
    if (dateOnly) {
      return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    }
  }

  return new Date(input);
}

/**
 * Check if a date is valid
 */
export function isValidDate(date: DateInput): boolean {
  const d = toDate(date);
  return !isNaN(d.getTime());
}

/**
 * Format a date to a calendar-date string (YYYY-MM-DD) in the local time zone.
 *
 * `toISOString()` would convert to UTC first, which shifts the date by one day
 * for part of every day in any zone that is not UTC.
 */
export function formatDateISO(date: DateInput): string {
  const d = toDate(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/** Today's calendar date in the local time zone, as YYYY-MM-DD. */
export function todayISO(): string {
  return formatDateISO(new Date());
}

/**
 * Format a date using Intl.DateTimeFormat
 */
export function formatDate(date: DateInput, options?: Intl.DateTimeFormatOptions): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  };
  
  return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(toDate(date));
}

/**
 * Format a date with time
 */
export function formatDateTime(date: DateInput, options?: Intl.DateTimeFormatOptions): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  
  return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(toDate(date));
}

/**
 * Format a date as relative time (e.g., "2 days ago", "in 3 hours")
 */
export function formatRelativeTime(date: DateInput, baseDate: DateInput = new Date()): string {
  const target = toDate(date);
  const base = toDate(baseDate);
  const diffMs = target.getTime() - base.getTime();
  
  // Convert to different units
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  const rtf = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

  // Return appropriate relative time
  if (Math.abs(diffYears) >= 1) {
    return rtf.format(diffYears, 'year');
  } else if (Math.abs(diffMonths) >= 1) {
    return rtf.format(diffMonths, 'month');
  } else if (Math.abs(diffWeeks) >= 1) {
    return rtf.format(diffWeeks, 'week');
  } else if (Math.abs(diffDays) >= 1) {
    return rtf.format(diffDays, 'day');
  } else if (Math.abs(diffHours) >= 1) {
    return rtf.format(diffHours, 'hour');
  } else if (Math.abs(diffMinutes) >= 1) {
    return rtf.format(diffMinutes, 'minute');
  } else {
    return rtf.format(diffSeconds, 'second');
  }
}

/**
 * Add days to a date
 */
export function addDays(date: DateInput, days: number): Date {
  const result = new Date(toDate(date));
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Add months to a date
 */
export function addMonths(date: DateInput, months: number): Date {
  const result = new Date(toDate(date));
  result.setMonth(result.getMonth() + months);
  return result;
}

/**
 * Add years to a date
 */
export function addYears(date: DateInput, years: number): Date {
  const result = new Date(toDate(date));
  result.setFullYear(result.getFullYear() + years);
  return result;
}

/**
 * Subtract days from a date
 */
export function subtractDays(date: DateInput, days: number): Date {
  return addDays(date, -days);
}

/**
 * Get the start of day (00:00:00)
 */
export function startOfDay(date: DateInput): Date {
  const result = new Date(toDate(date));
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get the end of day (23:59:59.999)
 */
export function endOfDay(date: DateInput): Date {
  const result = new Date(toDate(date));
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Check if two dates are the same day
 */
export function isSameDay(date1: DateInput, date2: DateInput): boolean {
  const d1 = toDate(date1);
  const d2 = toDate(date2);
  
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
}

/**
 * Check if a date is today
 */
export function isToday(date: DateInput): boolean {
  return isSameDay(date, new Date());
}

/**
 * Check if a date is tomorrow
 */
export function isTomorrow(date: DateInput): boolean {
  return isSameDay(date, addDays(new Date(), 1));
}

/**
 * Check if a date is yesterday
 */
export function isYesterday(date: DateInput): boolean {
  return isSameDay(date, subtractDays(new Date(), 1));
}

/**
 * Check if a date is in the past
 */
export function isPast(date: DateInput): boolean {
  return toDate(date).getTime() < new Date().getTime();
}

/**
 * Check if a date is in the future
 */
export function isFuture(date: DateInput): boolean {
  return toDate(date).getTime() > new Date().getTime();
}

/**
 * Get the difference between two dates in days
 */
export function differenceInDays(date1: DateInput, date2: DateInput): number {
  const d1 = startOfDay(date1);
  const d2 = startOfDay(date2);
  const diffMs = d1.getTime() - d2.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Get the difference between two dates in hours
 */
export function differenceInHours(date1: DateInput, date2: DateInput): number {
  const d1 = toDate(date1);
  const d2 = toDate(date2);
  const diffMs = d1.getTime() - d2.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60));
}

/**
 * Get the difference between two dates in minutes
 */
export function differenceInMinutes(date1: DateInput, date2: DateInput): number {
  const d1 = toDate(date1);
  const d2 = toDate(date2);
  const diffMs = d1.getTime() - d2.getTime();
  return Math.floor(diffMs / (1000 * 60));
}

/**
 * Parse a date string in various formats
 */
export function parseDate(dateString: string): Date | null {
  if (!dateString) return null;
  
  // Try parsing as ISO string first
  const isoDate = new Date(dateString);
  if (isValidDate(isoDate)) return isoDate;
  
  // Try parsing common formats
  const formats = [
    /^\d{4}-\d{2}-\d{2}$/,  // YYYY-MM-DD
    /^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY
    /^\d{2}-\d{2}-\d{4}$/,  // MM-DD-YYYY
  ];
  
  for (const format of formats) {
    if (format.test(dateString)) {
      const parsed = new Date(dateString);
      if (isValidDate(parsed)) return parsed;
    }
  }
  
  return null;
}

/**
 * Get a user-friendly date string
 */
export function formatFriendlyDate(date: DateInput): string {
  const d = toDate(date);
  
  if (isToday(d)) {
    return 'Today';
  } else if (isTomorrow(d)) {
    return 'Tomorrow';
  } else if (isYesterday(d)) {
    return 'Yesterday';
  } else {
    const daysDiff = differenceInDays(d, new Date());
    if (Math.abs(daysDiff) <= 7) {
      return formatRelativeTime(d);
    } else {
      return formatDate(d);
    }
  }
}

/**
 * Format date for input[type="date"] elements
 */
export function formatDateForInput(date: DateInput): string {
  return formatDateISO(date);
}

/**
 * Get the current timestamp in ISO format
 */
export function now(): string {
  return new Date().toISOString();
}

/**
 * Create a date from components
 */
export function createDate(year: number, month: number, day: number): Date {
  return new Date(year, month - 1, day); // Month is 0-indexed
}

/**
 * Get the day of the week (0 = Sunday, 6 = Saturday)
 */
export function getDayOfWeek(date: DateInput): number {
  return toDate(date).getDay();
}

/**
 * Get the week number of the year
 */
export function getWeekNumber(date: DateInput): number {
  const d = new Date(toDate(date));
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}

/**
 * Format date for display in different contexts
 */
export const formatters = {
  short: (date: DateInput) => formatDate(date, { month: 'short', day: 'numeric' }),
  long: (date: DateInput) => formatDate(date, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
  time: (date: DateInput) => formatDate(date, { hour: '2-digit', minute: '2-digit' }),
  datetime: (date: DateInput) => formatDateTime(date),
  relative: (date: DateInput) => formatRelativeTime(date),
  friendly: (date: DateInput) => formatFriendlyDate(date),
  iso: (date: DateInput) => formatDateISO(date),
  input: (date: DateInput) => formatDateForInput(date)
};